import { createContext, useContext, useReducer, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import LoadingPage from '../components/loader';

const ACCESS_TOKEN_EXPIRY = 3600; // 1 hour
const REFRESH_TOKEN_EXPIRY = 604800; // 1 week

const initialAuthState = {
  isAuthenticated: false,
  user: null,
  role: null,
  roleType: null,
  permissions: [],
  loading: true,
  error: null,
  tokenExpiry: null
};

const authReducer = (state, action) => {
  switch (action.type) {
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        role: action.payload.role,
        roleType: action.payload.roleType,
        permissions: action.payload.permissions,
        tokenExpiry: Date.now() + (action.payload.expiresIn * 1000),
        loading: false,
        error: null
      };
    case 'TOKEN_REFRESHED':
      return {
        ...state,
        tokenExpiry: Date.now() + (action.payload.expiresIn * 1000),
        error: null
      };
    case 'LOGIN_FAILURE':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        role: null,
        roleType: null,
        permissions: [],
        tokenExpiry: null,
        loading: false,
        error: action.payload
      };
    case 'LOGOUT':
      return {
        ...initialAuthState,
        loading: false
      };
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload
      };
    default:
      return state;
  }
};

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [authState, dispatch] = useReducer(authReducer, initialAuthState);
  const [refreshPromise, setRefreshPromise] = useState(null);

  useEffect(() => {
    const originalFetch = window.fetch;
    
    window.fetch = async (url, options = {}) => {
      if (url.includes('/api/auth/')) {
        return originalFetch(url, options);
      }

      const token = localStorage.getItem('auth_token');
      const headers = {
        ...options.headers,
        'Content-Type': 'application/json'
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      let response = await originalFetch(url, { ...options, headers });
      
      if (response.status === 401) {
        if (!refreshPromise) {
          const promise = handleTokenRefresh();
          setRefreshPromise(promise);
          
          try {
            const newTokens = await promise;
            if (newTokens) {
              headers['Authorization'] = `Bearer ${newTokens.access_token}`;
              response = await originalFetch(url, { ...options, headers });
            } else {
              dispatch({ type: 'LOGOUT' });
              localStorage.removeItem('auth_token');
              localStorage.removeItem('auth_role');
              localStorage.removeItem('user_email');
              
              window.dispatchEvent(new CustomEvent('apiError', { 
                detail: { status: 401 } 
              }));
              
              return response; 
            }
          } finally {
            setRefreshPromise(null);
          }
        } else {
          const newTokens = await refreshPromise;
          if (newTokens) {
            headers['Authorization'] = `Bearer ${newTokens.access_token}`;
            response = await originalFetch(url, { ...options, headers });
          }
        }
      }
      
      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, [refreshPromise]);

  const checkAuthStatus = async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        dispatch({ type: 'SET_LOADING', payload: false });
        return;
      }

      const response = await fetch('/api/auth/verify', {
        method: 'GET',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 401) {
        window.dispatchEvent(new CustomEvent('apiError', { 
          detail: { status: 401 } 
        }));
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      if (!responseData.data) {
        throw new Error('Invalid response structure');
      }

      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          user: {
            id: responseData.data.data.user_id,
            username: responseData.data.data.username,
            fullName: responseData.data.data.full_name,
            email: responseData.data.data.email
          },
          role: responseData.data.data.role,
          roleType: responseData.data.data.role_type,
          permissions: responseData.data.data.permissions || [],
          expiresIn: ACCESS_TOKEN_EXPIRY
        }
      });
    } catch (error) {
      console.error('Auth check failed:', error);
      dispatch({ 
        type: 'LOGIN_FAILURE',
        payload: error.message || 'Failed to authenticate. Please try again.'
      });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };
  
  const handleTokenRefresh = async () => {
    try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
            throw new Error('No refresh token available');
        }
        
        const response = await fetch('/api/auth/refresh', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ refresh_token: refreshToken })
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Refresh failed');
        }

        const { data } = await response.json();
        
        if (!data?.access_token) {
            throw new Error('Invalid refresh response');
        }
        
        // Store the new tokens
        localStorage.setItem('auth_token', data.access_token);
        if (data.refresh_token) {
            localStorage.setItem('refresh_token', data.refresh_token);
        }
        
        dispatch({
            type: 'TOKEN_REFRESHED',
            payload: {
                expiresIn: data.expires_in || ACCESS_TOKEN_EXPIRY
            }
        });
        
        return { 
            access_token: data.access_token,
            expiresIn: data.expires_in || ACCESS_TOKEN_EXPIRY
        };
    } catch (error) {
        console.error('Token refresh failed:', error);
        dispatch({ type: 'LOGOUT' });
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        return null;
    }
};

  const login = async (credentials, loginType = 'admin') => {
    dispatch({ type: 'SET_LOADING', payload: true });
    
    try {
      const endpoint = loginType === 'admin' 
        ? '/api/auth/admin/login' 
        : '/api/auth/user/login';
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials)
      });
  
      const responseText = await response.text();
  
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        throw new Error('Invalid server response format');
      }
  
      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }
  
      if (!data || !data.data || !data.data.token) {
        throw new Error('Invalid server response structure');
      }
      localStorage.setItem('auth_token', data.data.token);
      localStorage.setItem('auth_role', data.data.role);
      localStorage.setItem('user_email', data.data.user.email);
      localStorage.setItem('user_name', data.data.user.username);
      
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          user: {
            id: data.data.user.id,
            username: data.data.user.username,
            fullName: data.data.user.fullName,
            email: data.data.user.email
          },
          role: data.data.role,
          roleType: data.data.user.roleType,
          permissions: data.data.permissions || [],
          expiresIn: ACCESS_TOKEN_EXPIRY
        }
      });
      
      return { 
        success: true, 
        role: data.data.role 
      };
    } catch (error) {
      console.error('Login error:', error);
      dispatch({
        type: 'LOGIN_FAILURE',
        payload: error.message || 'Login failed. Please try again.'
      });
      return { 
        success: false, 
        error: error.message 
      };
    }
  };
  const logout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_role');
    localStorage.removeItem('user_email');
    dispatch({ type: 'LOGOUT' });
  };

  const hasPermission = (permissionName) => {
    return authState.isAuthenticated && 
      (authState.role === 'admin' || 
       authState.permissions.includes(permissionName));
  };

  const isAdmin = () => {
    return authState.isAuthenticated && authState.role === 'admin';
  };

  // Token expiry check
  useEffect(() => {
    checkAuthStatus();
    
    const interval = setInterval(() => {
      if (authState.tokenExpiry && Date.now() > authState.tokenExpiry - 30000) {
        handleTokenRefresh();
      }
    }, 60000);
    
    return () => clearInterval(interval);
  }, []);

  const value = {
    ...authState,
    login,
    logout,
    hasPermission,
    isAdmin,
    refreshToken: handleTokenRefresh
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const ProtectedRoute = ({ children, requiredPermission = null, adminOnly = false }) => {
  const { isAuthenticated, hasPermission, isAdmin, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingPage />;
  }

  if (!isAuthenticated) {
    const isAdminPath = location.pathname.startsWith('/admin');
    const loginPath = isAdminPath ? '/admin/login' : '/user/login';
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  if (adminOnly && !isAdmin()) {
    return <Navigate to="/unauthorized" replace />;
  }

  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};